import React, { useState, useMemo, useEffect } from 'react';
import { Form } from 'antd';
import { useSelector } from 'react-redux';
import { Link, useRouteMatch } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Tabs, DropDownMenu, Popup, Rating, TextArea, notification, NOTIFICATION_TYPES } from '_atoms';
import { ACTIONS, UI, useAbility } from 'permission';
import history from 'helpers/history';
import { changeRole, postFeedback, signOut } from 'actions';
import { getUser } from 'store/selectors/authorizeSelectors';
import { ROUTES } from 'router/constants';
import { Typography, TYPOGRAPHY_WEIGHT } from '_atoms/Typography';
import { REQUIRED_RULE } from '_organisms/InterviewFeedback/constants';
import { ReactComponent as Arrow } from 'dist/icons/caret-down.svg';
import { ReactComponent as Logo } from 'dist/icons/logo.svg';
import { ReactComponent as CheckMark } from 'dist/icons/checkmark.svg';
import { ReactComponent as Person } from 'dist/icons/person.svg';
import { ReactComponent as Logout } from 'dist/icons/logout.svg';
import avatarPlaceholder from 'dist/icons/avatar-placeholder.svg';
import { useHeaderTabs } from './utils';
import './header.scss';

interface PopupSendFeedbackProps {
  open: boolean;
  onClose: () => void;
}

const PopupSendFeedback: React.FC<PopupSendFeedbackProps> = ({ open, onClose }) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();

  const onFinish = (data: Record<string, unknown>) => {
    postFeedback(data)
      .then(() => {
        notification({
          type: NOTIFICATION_TYPES.SUCCESS,
          message: t('translation:popup_send_feedback.notification_success'),
        });
        onClose();
      })
      .catch(() => {});
  };

  useEffect(() => {
    form.resetFields();
  }, [open]);

  return (
    <Popup
      className="popup-send-feedback"
      open={open}
      title={t('translation:popup_send_feedback.title')}
      cancelButtonProps={{ onClick: onClose, children: t('translation:popup_send_feedback.button_cancel') }}
      okButtonProps={{
        children: t('translation:popup_send_feedback.button_ok'),
        form: 'form-send-feedback',
        htmlType: 'submit',
      }}
    >
      <Form name="form-send-feedback" form={form} initialValues={{ message: '' }} onFinish={onFinish}>
        <Rating
          label={t('translation:popup_send_feedback.title_rating')}
          itemProps={{ name: 'rating', rules: [{ required: true, message: 'Please rate' }] }}
        />
        <TextArea
          label={t('translation:popup_send_feedback.title_comment')}
          placeholder={t('translation:popup_send_feedback.placeholder_comment')}
          itemProps={{ name: 'message', rules: [REQUIRED_RULE] }}
        />
      </Form>
    </Popup>
  );
};

export const Header: React.FC = () => {
  const { t } = useTranslation();
  const ability = useAbility();
  const { path } = useRouteMatch();
  const user = useSelector(getUser);

  const [openPopupSendFeedback, setOpenPopupSendFeedback] = useState(false);

  const isAnalyticsAble = user && ability.can(ACTIONS.READ, UI.ANALYTICS_PAGE);
  const isJobsAble = user && ability.can(ACTIONS.READ, UI.JOBS_PAGE);
  const isCandidatesAble = user && ability.can(ACTIONS.READ, UI.CANDIDATES_PAGE);

  const HEADER_TABS = useHeaderTabs(t);

  const tabs = useMemo(() => {
    const hidden = new Set();
    if (!isAnalyticsAble) hidden.add(HEADER_TABS.ANALYTICS);
    if (!isJobsAble) hidden.add(HEADER_TABS.JOBS);
    if (!isCandidatesAble) hidden.add(HEADER_TABS.CANDIDATES);
    return Object.values(HEADER_TABS)
      .filter(name => !hidden.has(name))
      .map(name => ({ key: name.toLowerCase(), label: name }));
  }, [isAnalyticsAble, isJobsAble, isCandidatesAble]);

  const setActiveTab = (tab: string) => history.push(`/${tab}`);
  const activeRole = user?.active_role;
  const roles = user?.roles || [];

  const onChangeRole = (active_role: number) => () => {
    if (active_role !== activeRole.pk) {
      changeRole(user.uuid, { active_role } as unknown as { role: number | null });
    }
  };

  const onOpenFeedbackPopup = () => setOpenPopupSendFeedback(true);
  const onCloseFeedbackPopup = () => setOpenPopupSendFeedback(false);

  return (
    <div className="header-block">
      <div className="header-block__content">
        <Link to={ROUTES.DASHBOARD} className="header-block__logo">
          <Logo />
        </Link>
        <Tabs activeTab={path.slice(1)} onTabClick={setActiveTab} items={tabs} className="header-block__tabs" />
        <div className="header-block__logout">
          {roles.length > 1 && (
            <div className="change-role">
              <DropDownMenu
                className="change-role__menu"
                triggerElement={
                  <div className="change-role__trigger">
                    <Typography.Text className="change-role__trigger-text" weight={TYPOGRAPHY_WEIGHT.BOLD}>
                      {activeRole.name}
                    </Typography.Text>
                    <Arrow className="change-role__trigger-icon" />
                    <Person className="change-role__trigger-icon-mobile" />
                  </div>
                }
              >
                {roles.map(role => {
                  const companyAndService =
                    role.company && role.service
                      ? `${role.company.name} - ${role.service.name}`
                      : role.service
                      ? role.service.name
                      : role.company?.name;
                  return (
                    <li key={role.pk} role="none" onClick={onChangeRole(role.pk)}>
                      <div className="role-name">{role.name}</div>
                      <div className="role-info">{companyAndService}</div>
                      {role.pk === activeRole.pk && <CheckMark className="role-checkmark" />}
                    </li>
                  );
                })}
              </DropDownMenu>
            </div>
          )}
          <DropDownMenu
            className="profile__menu"
            triggerElement={
              <div className="profile__trigger">
                <img
                  className="profile__trigger-avatar"
                  src={user.profile.avatar || avatarPlaceholder}
                  alt="avatar"
                  onError={e => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = avatarPlaceholder;
                  }}
                />
                <Arrow className="profile__trigger-icon" />
              </div>
            }
          >
            <li>
              <Typography.Text weight={TYPOGRAPHY_WEIGHT.BOLD}>{user.full_name}</Typography.Text>
              <Typography.Caption>{user.email}</Typography.Caption>
            </li>
            <li>
              <button type="button" onClick={onOpenFeedbackPopup}>
                Send feedback
              </button>
            </li>
            <li>
              <button type="button" className="logout" onClick={signOut}>
                <Logout />
                {t('translation:header.button_logout')}
              </button>
            </li>
          </DropDownMenu>
        </div>
      </div>
      <PopupSendFeedback open={openPopupSendFeedback} onClose={onCloseFeedbackPopup} />
    </div>
  );
};
